/**
 * "Is this document busy?" — one answer, claimed before the round trip rather than after it.
 *
 * Every action a Documents surface starts on a document is exclusive: a second Stop, a second
 * reprocess or a second answer to the same parked question either duplicates a job or races
 * the first one to a refusal the person never asked for. The guard cannot be React state,
 * because two presses in the same frame both read it before the re-render that would have
 * disabled the button — so the ref is the authority and the state is only its render.
 *
 * Callers claim, run, and release in a `finally`: a document left claimed by a failed call is
 * a row nobody can repair.
 */

import { useCallback, useMemo, useRef, useState } from "react";

const NO_PENDING: ReadonlySet<string> = new Set();

export type PendingArtifactClaims = {
  /**
   * Takes all of these documents, or none of them — `false` means at least one was already
   * claimed, and the caller should do nothing rather than report anything: the only way to
   * reach it is a press on a control the screen is already showing as busy.
   */
  claim: (artifactIds: readonly string[]) => boolean;
  /** Hands the documents back, whichever way the round trip ended. */
  release: (artifactIds: readonly string[]) => void;
  /** The claimed set, for a surface that renders the whole of it. */
  pendingArtifactIds: ReadonlySet<string>;
  /** The claimed set, for the row that only asks about itself. */
  isPending: (artifactId: string) => boolean;
};

export function usePendingArtifactClaims(): PendingArtifactClaims {
  const pendingRef = useRef<Set<string>>(new Set());
  const [pendingArtifactIds, setPendingArtifactIds] = useState<ReadonlySet<string>>(NO_PENDING);

  const claim = useCallback((artifactIds: readonly string[]): boolean => {
    if (artifactIds.some((artifactId) => pendingRef.current.has(artifactId))) return false;
    for (const artifactId of artifactIds) pendingRef.current.add(artifactId);
    setPendingArtifactIds(new Set(pendingRef.current));
    return true;
  }, []);

  const release = useCallback((artifactIds: readonly string[]) => {
    for (const artifactId of artifactIds) pendingRef.current.delete(artifactId);
    setPendingArtifactIds(new Set(pendingRef.current));
  }, []);

  const isPending = useCallback(
    (artifactId: string) => pendingArtifactIds.has(artifactId),
    [pendingArtifactIds]
  );

  return useMemo(
    () => ({ claim, release, pendingArtifactIds, isPending }),
    [claim, isPending, pendingArtifactIds, release]
  );
}
